import React, { useState } from 'react';
import { CheckCircle, Shield, Lock, Globe, Smartphone, Infinity, Headphones, DollarSign, Calendar, RotateCcw } from 'lucide-react';

const ProductHero = ({ onPurchase }) => {
 const [selectedPlan, setSelectedPlan] = useState('renewal');
 const [selectedDuration, setSelectedDuration] = useState('3');

 const plans = {
 renewal: {
 id: 'renewal',
 name: 'Official Account Renewal (1 Person)',
 price: '$17.90/month',
 fullPrice: '$53.70',
 period: 'pay 3 months upfront',
 description: 'Extend existing Plus access, no account change needed'
 },
 official: {
 id: 'official',
 name: 'Official Independent Account (Full Features)',
 price: '$25.00/month',
 fullPrice: '$25.00',
 period: '1 month',
 description: 'Complete new ChatGPT Plus account with full features'
 },
 shared: {
 id: 'shared',
 name: 'Official Shared Account (Multi-user)',
 price: '$10.00/month',
 fullPrice: '$60.00',
 period: '6 months',
 description: 'Cost-effective shared access for multiple users'
 }
 };

 const valuePoints = [
 { icon: <CheckCircle className="w-5 h-5" />, text: '最新：Latest AI Models - GPT-4o, o3, GPT-4.1, GPT-4.5 + all future updates' },
 { icon: <DollarSign className="w-5 h-5" />, text: '省钱：Save $192/year - Pay $8.99 instead of $25/month' },
 { icon: <Shield className="w-5 h-5" />, text: '权限：Full premium access - No limitations, same capabilities as official subscribers' }
 ];

 const highlightFeatures = [
 { icon: <DollarSign className="w-4 h-4" />, text: 'Only $17.90/month, pay 3 months upfront: $53.70' },
 { icon: <Calendar className="w-4 h-4" />, text: 'Extend existing Plus access, no account change needed' },
 { icon: <Lock className="w-4 h-4" />, text: 'Personal exclusive, records visible to you only' },
 { icon: <Shield className="w-4 h-4" />, text: 'Official authorization, account security guaranteed' },
 { icon: <CheckCircle className="w-4 h-4" />, text: 'GPT-4o, o3, GPT-4.1, GPT-4.5 + all future updates' },
 { icon: <Infinity className="w-4 h-4" />, text: 'Full premium access - No limitations' }
 ];

 const priceComparison = [
 { label: 'Official ChatGPT Plus', price: '$25/month', isOfficial: true },
 { label: 'Our Shared Access', price: '$8.99/month', isOurs: true },
 { label: 'You Save', price: '$192 per year', isSavings: true }
 ];

 const durations = [
 { id: '1', label: '1 Month', price: '$19.90', total: '$19.90' },
 { id: '3', label: '3 Months', price: '$17.90', total: '$53.70', badge: 'Popular' },
 { id: '12', label: '12 Months', price: '$15.90', total: '$190.80', badge: 'Best Value' }
 ];

 const getCurrentPrice = () => {
 const duration = durations.find(d => d.id === selectedDuration);
 return duration || durations[1];
 };

 return (
 <section className="py-16 bg-white">
 <div className="container mx-auto px-4">
 {/* Header */}
 <div className="text-center mb-12">
 <h1 className="text-4xl md:text-6xl font-bold text-ink-900 mb-6 leading-tight">
 Access Latest GPT-4o, o3 & All Premium Models
 <span className="block text-black mt-2">65% Less Cost</span>
 </h1>
 <p className="text-xl text-ink-600 max-w-4xl mx-auto leading-relaxed">
 Get instant access to OpenAI's most advanced AI models including GPT-4o, o3, GPT-4.1, and GPT-4.5. 
 Same cutting-edge technology, fraction of the price.
 </p>
 </div>


 {/* Main Content - Two Column Layout */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
 {/* Left Column - Product Information */}
 <div>
 {/* Highlighted Recommendation */}
 <div className="border-2 border-black rounded-2xl p-8 bg-white mb-8">
 <h2 className="text-2xl font-bold text-black mb-6 flex items-center">
 🔥 Recommended: Official Account Renewal (1 Person)
 </h2>
 <ul className="space-y-4">
 {highlightFeatures.map((feature, index) => (
 <li key={index} className="flex items-start space-x-3 p-3 border-b border-ink-200 last:border-b-0">
 <span className="text-black mt-1 flex-shrink-0">{feature.icon}</span>
 <span className="text-ink-700">{feature.text}</span>
 </li>
 ))}
 </ul>
 </div>

 </div>

 {/* Right Column - Purchase Panel (Sticky) */}
 <div className="lg:sticky lg:top-6 lg:h-fit">
 {/* Duration Selection */}
 <div className="bg-white border-2 border-black rounded-2xl p-6 mb-6 shadow-lg">
 <h3 className="text-lg font-semibold mb-4 text-center">Choose Your Plan</h3>
 <div className="grid grid-cols-3 gap-3">
 {durations.map((duration) => (
 <div
 key={duration.id}
 onClick={() => setSelectedDuration(duration.id)}
 className={`relative p-4 border-2 rounded-2xl cursor-pointer transition-all duration-200 ${
 selectedDuration === duration.id
 ? 'border-black bg-black text-white'
 : 'border-ink-200 bg-white hover:border-black'
 }`}
 >
 {duration.badge && (
 <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
 <span className="bg-black text-white px-3 py-1.5 rounded-full text-xs font-bold border-2 border-white shadow-lg whitespace-nowrap">
 {duration.badge}
 </span>
 </div>
 )}
 <div className="text-center">
 <div className="font-semibold text-sm">{duration.label}</div>
 <div className="text-lg font-bold">{duration.price}/mo</div>
 <div className="text-xs opacity-80">Total: {duration.total}</div>
 </div>
 </div>
 ))}
 </div>
 </div>

 <div className="bg-white border-2 border-black rounded-2xl p-8 shadow-lg">
 <h2 className="text-2xl font-bold text-center mb-6">Purchase Information</h2>
 
 <div className="flex justify-between items-center mb-8 p-5 border-b-2 border-ink-200">
 <span className="text-4xl font-light text-black">
 {getCurrentPrice().price}/mo
 </span>
 <span className="bg-black text-white px-4 py-2 rounded-full text-sm font-medium">
 In Stock
 </span>
 </div>

 <div className="text-center mb-4">
 <div className="text-sm text-ink-600">Total Payment</div>
 <div className="text-2xl font-bold text-black">{getCurrentPrice().total}</div>
 </div>

 <button
 onClick={() => onPurchase(selectedPlan)}
 className="w-full bg-black text-white py-5 px-6 rounded-2xl text-xl font-semibold hover:bg-ink-800 transition-all duration-200 mb-6"
 >
 Buy Now
 </button>

 <div className="text-center text-ink-600 mb-6 leading-relaxed">
 After purchase, we will collect your binding email address via popup window
 </div>

 {/* Security Badges */}
 <div className="flex flex-wrap justify-center gap-6 text-sm">
 <span className="flex items-center space-x-2 text-ink-600">
 <Lock className="w-4 h-4" />
 <span>SSL Encrypted</span>
 </span>
 <span className="flex items-center space-x-2 text-ink-600">
 <CheckCircle className="w-4 h-4" />
 <span>Secure Payment</span>
 </span>
 <span className="flex items-center space-x-2 text-ink-600">
 <Headphones className="w-4 h-4" />
 <span>After-sales Support</span>
 </span>
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>
 );
};

export default ProductHero;